import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ReimbursementForm = () => {
  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    name: "",
    managerName: "",
    department: "",
    location: "",
    employeeName: "",
    clientName: "",
    purpose: "",
    expenses: [
      {
        date: "",
        description: "",
        lodging: "",
        boarding: "",
        food: "",
        conveyance: "",
        Material_Purchase: "",
        PO: "",
        Invoice: "",
        other: "",
        total: "0",
        note: "",
        files: [],
      },
    ],
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Calculate totals whenever expense fields change
  useEffect(() => {
    const updatedExpenses = formData.expenses.map((expense) => {
      const lodging = parseFloat(expense.lodging) || 0;
      const boarding = parseFloat(expense.boarding) || 0;
      const food = parseFloat(expense.food) || 0;
      const conveyance = parseFloat(expense.conveyance) || 0;
      const Material_Purchase = parseFloat(expense.Material_Purchase) || 0;
      const PO = parseFloat(expense.PO) || 0;
      const Invoice = parseFloat(expense.Invoice) || 0;
      const other = parseFloat(expense.other) || 0;

      const total =
        lodging +
        boarding +
        food +
        conveyance +
        Material_Purchase +
        PO +
        Invoice +
        other;

      return {
        ...expense,
        total: total.toString(),
      };
    });

    setFormData((prev) => ({
      ...prev,
      expenses: updatedExpenses,
    }));
  }, [formData.expenses]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes("expenses")) {
      const [key, index] = name.split(".");
      const newExpenses = [...formData.expenses];
      newExpenses[index] = { ...newExpenses[index], [key]: value };
      setFormData({ ...formData, expenses: newExpenses });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleExpenseChange = (index, e) => {
    const { name, value } = e.target;
    const newExpenses = [...formData.expenses];
    newExpenses[index] = { ...newExpenses[index], [name]: value };
    setFormData({ ...formData, expenses: newExpenses });
  };

  const handleFileChange = (index, e) => {
    const files = Array.from(e.target.files);
    const newExpenses = [...formData.expenses];
    newExpenses[index].files = files;
    setFormData({ ...formData, expenses: newExpenses });
  };

  const addExpense = () => {
    setFormData({
      ...formData,
      expenses: [
        ...formData.expenses,
        {
          date: "",
          description: "",
          lodging: "",
          boarding: "",
          food: "",
          conveyance: "",
          Material_Purchase: "",
          PO: "",
          Invoice: "",
          other: "",
          total: "0",
          note: "",
          files: [],
        },
      ],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess("");

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Validate only employeeName is required
      if (!formData.employeeName.trim()) {
        throw new Error("Employee Name is required");
      }

      const formDataToSend = new FormData();

      // Append all fields but none are required except employeeName
      formDataToSend.append("fromDate", formData.fromDate);
      formDataToSend.append("toDate", formData.toDate);
      formDataToSend.append("name", formData.name);
      formDataToSend.append("managerName", formData.managerName);
      formDataToSend.append("department", formData.department);
      formDataToSend.append("location", formData.location);
      formDataToSend.append("employeeName", formData.employeeName);
      formDataToSend.append("clientName", formData.clientName);
      formDataToSend.append("purpose", formData.purpose);

      formData.expenses.forEach((expense, index) => {
        formDataToSend.append(`expenses[${index}][date]`, expense.date);
        formDataToSend.append(
          `expenses[${index}][description]`,
          expense.description
        );
        formDataToSend.append(
          `expenses[${index}][lodging]`,
          expense.lodging || "0"
        );
        formDataToSend.append(
          `expenses[${index}][boarding]`,
          expense.boarding || "0"
        );
        formDataToSend.append(`expenses[${index}][food]`, expense.food || "0");
        formDataToSend.append(
          `expenses[${index}][conveyance]`,
          expense.conveyance || "0"
        );
        formDataToSend.append(
          `expenses[${index}][Material_Purchase]`,
          expense.Material_Purchase || "0"
        );
        formDataToSend.append(`expenses[${index}][PO]`, expense.PO || "0");
        formDataToSend.append(
          `expenses[${index}][Invoice]`,
          expense.Invoice || "0"
        );
        formDataToSend.append(
          `expenses[${index}][other]`,
          expense.other || "0"
        );
        formDataToSend.append(
          `expenses[${index}][total]`,
          expense.total || "0"
        );
        formDataToSend.append(`expenses[${index}][note]`, expense.note);

        expense.files.forEach((file, fileIndex) => {
          formDataToSend.append(`expenses[${index}][files][]`, file);
        });
      });

      const response = await fetch(`${BASE_URL}/api/reimbursement/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error submitting reimbursement");
      }

      setSuccess("Reimbursement submitted successfully");

      // Reset form data after successful submission
      setFormData({
        fromDate: "",
        toDate: "",
        name: "",
        managerName: "",
        department: "",
        location: "",
        employeeName: "",
        clientName: "",
        purpose: "",
        expenses: [
          {
            date: "",
            description: "",
            lodging: "",
            boarding: "",
            food: "",
            conveyance: "",
            Material_Purchase: "",
            PO: "",
            Invoice: "",
            other: "",
            total: "0",
            note: "",
            files: [],
          },
        ],
      });

      document.querySelectorAll('input[type="file"]').forEach((input) => {
        input.value = "";
      });
    } catch (err) {
      console.error("Error submitting reimbursement:", err);
      setError(err.message || "Error submitting reimbursement");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = formData.expenses.reduce(
    (sum, exp) => sum + (parseFloat(exp.total) || 0),
    0
  );

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Reimbursement Request"
        subtitle="Submit your expense reimbursement request"
        icon={<ReceiptIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Request Details Card */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              mb: 2,
              pb: 1.5,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            Request Details
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date"
                type="date"
                name="fromDate"
                value={formData.fromDate}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date"
                type="date"
                name="toDate"
                value={formData.toDate}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.fromDate }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Manager Name"
                name="managerName"
                value={formData.managerName}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Manager's name"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Employee Name"
                name="employeeName"
                value={formData.employeeName}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Employee name"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Purpose of reimbursement"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Expense Details Section */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            borderRadius: "16px",
            p: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 1.5,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e" }}
            >
              Expense Details
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addExpense}
              sx={{
                borderColor: "#667eea",
                color: "#667eea",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { borderColor: "#5a6fd8", bgcolor: "#eef0ff" },
              }}
            >
              Add Expense
            </Button>
          </Box>

          {formData.expenses.map((expense, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                border: "1px solid #eef0f6",
                borderRadius: "10px",
                p: 2.5,
                mb: 2,
                bgcolor: "#FAFBFC",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#5a5f7a",
                  }}
                >
                  Expense #{index + 1}
                </Typography>
                {formData.expenses.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        expenses: formData.expenses.filter(
                          (_, i) => i !== index
                        ),
                      })
                    }
                    sx={{
                      color: "#EF4444",
                      "&:hover": { bgcolor: "#FEE2E2" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date"
                    type="date"
                    name="date"
                    value={expense.date}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0],
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Description"
                    name="description"
                    value={expense.description}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    placeholder="Expense description"
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Conveyance"
                    type="number"
                    name="conveyance"
                    value={expense.conveyance}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Material Purchase"
                    type="number"
                    name="Material_Purchase"
                    value={expense.Material_Purchase}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Other"
                    type="number"
                    name="other"
                    value={expense.other}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Total"
                    type="number"
                    name="total"
                    value={expense.total}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-root": {
                        bgcolor: "#f5f7fb",
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <TextField
                    label="Note"
                    name="note"
                    value={expense.note}
                    onChange={(e) => handleExpenseChange(index, e)}
                    fullWidth
                    size="small"
                    placeholder="Additional notes"
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={4}>
                  <Button
                    component="label"
                    variant="outlined"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      height: "40px",
                      borderColor: "#CBD5E1",
                      color: "#8b8fa3",
                      textTransform: "none",
                      borderRadius: "8px",
                      justifyContent: "flex-start",
                      "&:hover": { borderColor: "#8b8fa3", bgcolor: "#f5f7fb" },
                    }}
                  >
                    {expense.files && expense.files.length > 0
                      ? `${expense.files.length} file(s) selected`
                      : "Upload Receipts"}
                    <input
                      type="file"
                      id={`files-${index}`}
                      name={`files-${index}`}
                      multiple
                      onChange={(e) => handleFileChange(index, e)}
                      hidden
                    />
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {/* Grand Total */}
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: "1rem", color: "#5a5f7a" }}
            >
              Grand Total:
            </Typography>
            <Typography
              sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1a1a2e" }}
            >
              {"\u20B9"}{grandTotal.toFixed(2)}
            </Typography>
          </Box>
        </Paper>

        {/* Submit */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
            }
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              borderRadius: "10px",
              fontSize: "0.95rem",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
            }}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </Box>
      </form>
    </PageContainer>
  );
};

export default ReimbursementForm;
